import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowSquareOut, MagnifyingGlass } from '@phosphor-icons/react';
import {
    useEffect,
    useMemo,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/lib/auth/AuthContext';
import { ROUTE_ROLES } from '@/lib/auth/permissions';
import type { ConsoleRole } from '@/lib/auth/types';
import { cn } from '@/lib/utils/cn';
import { Kbd } from '@/ui/kbd';

interface PaletteItem {
    label: string;
    hint: string;
    href: string;
    external?: boolean;
    /** When set, the item is only shown if the current user holds one of the
     *  listed roles. Mirrors RequireRole so the palette doesn't surface a
     *  page that would just bounce on a ForbiddenPage. */
    requires?: readonly ConsoleRole[];
}

/**
 * Booking numbers are formatted `MV-YYYYMMDD-XXXXXX` (date prefix + ~6-char
 * suffix, e.g. MV-20260523-N4FU8P). Accept the leading dash being typed or
 * skipped and case-insensitively, and tolerate the internal dash either way.
 * Returns the upper-cased canonical form if the input is a plausible booking
 * number, null otherwise. The detail page itself decides 404 vs found —
 * the palette only proposes the jump.
 */
const BOOKING_PATTERN = /^mv-?([a-z0-9-]{4,30})$/i;
function normaliseBookingNumber(raw: string): string | null {
    const suffix = raw.trim().match(BOOKING_PATTERN)?.[1];
    return suffix ? `MV-${suffix.toUpperCase()}` : null;
}

const ITEMS: PaletteItem[] = [
    { label: 'Dashboard', hint: 'Overview, KPIs', href: '/' },
    { label: 'Bookings', hint: 'Recent reservations', href: '/bookings' },
    { label: 'Quotes', hint: 'Recent quotes', href: '/quotes' },
    { label: 'Settlements', hint: 'Invoices and payouts', href: '/settlements' },
    {
        label: 'Webhooks',
        hint: 'Endpoints and deliveries',
        href: '/webhooks',
        requires: ROUTE_ROLES['/webhooks'],
    },
    {
        label: 'API keys',
        hint: 'Issue, revoke',
        href: '/api-keys',
        requires: ROUTE_ROLES['/api-keys'],
    },
    {
        label: 'Audit log',
        hint: 'Every API call, filterable',
        href: '/audit',
        requires: ROUTE_ROLES['/audit'],
    },
    {
        label: 'Team',
        hint: 'Members and roles',
        href: '/team',
        requires: ROUTE_ROLES['/team'],
    },
    {
        label: 'Sandbox',
        hint: 'Reset and seed test data',
        href: '/sandbox',
        requires: ROUTE_ROLES['/sandbox'],
    },
    {
        label: 'Developer docs',
        hint: 'developers.safarapi.com',
        href: 'https://developers.safarapi.com',
        external: true,
    },
];

interface CommandPaletteProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Quick-nav palette opened from the Topbar's search affordance or ⌘K.
 * Navigation only — content search (booking by number, etc.) would need
 * dedicated backend search endpoints and isn't part of this scope.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(0);

    const visibleItems = useMemo(
        () =>
            ITEMS.filter(
                (item) => !item.requires || (user ? item.requires.includes(user.role) : false),
            ),
        [user],
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        // If the input looks like a booking number, propose the deep-link first.
        // Static items still appear underneath so the user can fall back on
        // navigation if they were trying to type something else.
        const bookingNumber = normaliseBookingNumber(query);
        const bookingItem: PaletteItem | null = bookingNumber
            ? {
                  label: `Open booking ${bookingNumber}`,
                  hint: 'Jump to the booking detail panel',
                  href: `/bookings?id=${encodeURIComponent(bookingNumber)}`,
              }
            : null;

        if (!q) return visibleItems;
        const staticMatches = visibleItems.filter(
            (i) => i.label.toLowerCase().includes(q) || i.hint.toLowerCase().includes(q),
        );
        return bookingItem ? [bookingItem, ...staticMatches] : staticMatches;
    }, [query, visibleItems]);

    useEffect(() => {
        if (open) {
            setQuery('');
            setActiveIdx(0);
        }
    }, [open]);

    useEffect(() => {
        if (activeIdx >= filtered.length) {
            setActiveIdx(Math.max(0, filtered.length - 1));
        }
    }, [filtered, activeIdx]);

    const selectItem = (item: PaletteItem) => {
        onOpenChange(false);
        if (item.external) {
            window.open(item.href, '_blank', 'noopener,noreferrer');
        } else {
            navigate(item.href);
        }
    };

    const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => Math.max(0, i - 1));
        } else if (e.key === 'Enter') {
            const item = filtered[activeIdx];
            if (item) {
                e.preventDefault();
                selectItem(item);
            }
        }
    };

    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
                <DialogPrimitive.Content
                    className="fixed left-1/2 top-[20vh] z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 overflow-hidden rounded-lg border border-border bg-card shadow-lg data-[state=open]:animate-fade-in"
                    aria-label="Command palette"
                >
                    <DialogPrimitive.Title className="sr-only">
                        Command palette
                    </DialogPrimitive.Title>
                    <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
                        <MagnifyingGlass
                            weight="regular"
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                        />
                        <input
                            autoFocus
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Jump to a page…"
                            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                            aria-controls="command-palette-list"
                            aria-activedescendant={
                                filtered[activeIdx]
                                    ? `command-item-${filtered[activeIdx].href}`
                                    : undefined
                            }
                        />
                        <Kbd>esc</Kbd>
                    </div>
                    <ul
                        id="command-palette-list"
                        role="listbox"
                        className="max-h-80 overflow-y-auto p-1.5"
                    >
                        {filtered.length === 0 ? (
                            <li className="px-3 py-6 text-center text-xs text-muted-foreground">
                                No matches for &ldquo;{query}&rdquo;
                            </li>
                        ) : (
                            filtered.map((item, idx) => (
                                <li
                                    key={item.href}
                                    id={`command-item-${item.href}`}
                                    role="option"
                                    aria-selected={idx === activeIdx}
                                    onMouseEnter={() => setActiveIdx(idx)}
                                    onClick={() => selectItem(item)}
                                    className={cn(
                                        'flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2 transition-colors',
                                        idx === activeIdx && 'bg-muted',
                                    )}
                                >
                                    <div className="flex flex-col">
                                        <span className="text-sm text-foreground">
                                            {item.label}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {item.hint}
                                        </span>
                                    </div>
                                    {item.external ? (
                                        <ArrowSquareOut
                                            weight="regular"
                                            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                                        />
                                    ) : null}
                                </li>
                            ))
                        )}
                    </ul>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
